/**
 * PhaseAccordion Component
 * Collapsible phase container with progress bar and status badge
 */

import { useState, useEffect } from 'react';
import {
  Paper,
  Group,
  Text,
  Badge,
  Progress,
  Collapse,
  UnstyledButton,
  Stack,
  Button,
} from '@mantine/core';
import { ChevronRight, ChevronDown, Plus } from 'react-feather';
import type { TaskWithDetails, TaskStatus, TaskPhase, TaskPhaseRecord } from '../../../../types';
import {
  calculatePhaseStatus,
  getPhaseStatusConfig,
  PHASE_DISPLAY_NAMES,
} from '../../../../config/taskConfig';
import { SubGroupSection } from './SubGroupSection';

interface PhaseAccordionProps {
  /** Phase record from task_phases table, or legacy phase enum value */
  phase: TaskPhaseRecord | TaskPhase;
  /** Tasks belonging to this phase (filtered for display) */
  tasks: TaskWithDetails[];
  /** All tasks for this phase (unfiltered — for status badge and progress calculations) */
  allTasks?: TaskWithDetails[];
  /** Whether a filter or search is currently active */
  isFilterActive?: boolean;
  /** Whether accordion is initially expanded (uncontrolled mode) */
  defaultExpanded?: boolean;
  /** Whether accordion is expanded (controlled mode) */
  expanded?: boolean;
  /** Callback when accordion is toggled (controlled mode) */
  onToggle?: () => void;
  /** Callback when task completion is toggled */
  onToggleComplete: (taskId: string, currentStatus: TaskStatus) => void;
  /** Callback when task is clicked */
  onTaskClick: (task: TaskWithDetails) => void;
  /** Callback when edit task is clicked */
  onEditTask?: (task: TaskWithDetails) => void;
  /** Callback when delete task is clicked */
  onDeleteTask?: (taskId: string) => void;
  /** Callback when duplicate task is clicked */
  onDuplicateTask?: (taskId: string) => void;
  /** Callback when add task is clicked */
  onAddTask?: (phase: TaskPhaseRecord | TaskPhase) => void;
  // MED-479: Subtask expansion props
  expandedTaskIds?: Set<string>;
  loadingTaskIds?: Set<string>;
  subtaskCache?: Record<string, TaskWithDetails[]>;
  onToggleExpand?: (taskId: string) => void;
  // MED-769: Per-phase expand all / collapse all
  onExpandAllTasks?: () => void;
  onCollapseAllTasks?: () => void;
  allTasksExpanded?: boolean;
  /** MED-770: Cohort ID for generating copy-link URLs */
  cohortId?: string;
}

export function PhaseAccordion({
  phase,
  tasks,
  allTasks,
  isFilterActive = false,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onToggle,
  onToggleComplete,
  onTaskClick,
  onEditTask,
  onDeleteTask,
  onDuplicateTask,
  onAddTask,
  expandedTaskIds,
  loadingTaskIds,
  subtaskCache,
  onToggleExpand,
  onExpandAllTasks,
  onCollapseAllTasks,
  allTasksExpanded,
  cohortId,
}: PhaseAccordionProps) {
  // Support both controlled (expanded + onToggle) and uncontrolled (defaultExpanded) modes
  const isControlled = controlledExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Sync internal state when controlled prop changes
  useEffect(() => {
    if (isControlled) {
      setInternalExpanded(controlledExpanded);
    }
  }, [isControlled, controlledExpanded]);

  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const handleToggle = () => {
    if (isControlled && onToggle) {
      onToggle();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  // Handle both TaskPhaseRecord and legacy TaskPhase enum
  const phaseName = typeof phase === 'string'
    ? PHASE_DISPLAY_NAMES[phase] || phase
    : phase.name;

  // Use unfiltered tasks for status badge and progress (always reflect full dataset)
  const statusTasks = allTasks ?? tasks;
  const completedCount = statusTasks.filter((t) => t.status === 'complete').length;
  const totalCount = statusTasks.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const filteredCount = tasks.length;

  // Calculate phase status from UNFILTERED data
  const phaseStatus = calculatePhaseStatus(statusTasks);
  const statusConfig = getPhaseStatusConfig(phaseStatus);

  // Group tasks by sub_group
  const subGroups = new Map<string, TaskWithDetails[]>();
  tasks.forEach((task) => {
    const groupName = task.sub_group || 'General';
    const existing = subGroups.get(groupName) || [];
    subGroups.set(groupName, [...existing, task]);
  });

  // Sort sub-groups by sort_order (General last), fallback to name for stable ordering
  const sortedSubGroups = Array.from(subGroups.entries()).sort((a, b) => {
    if (a[0] === 'General') return 1;
    if (b[0] === 'General') return -1;
    const aOrder = a[1][0]?.sub_group_sort_order ?? 999;
    const bOrder = b[1][0]?.sub_group_sort_order ?? 999;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a[0].localeCompare(b[0]);
  });

  // Determine progress bar color based on completion
  const progressBarColor = progressPercent === 100 ? '#28463A' : '#D1D69B';

  return (
    <Paper
      withBorder
      radius="md"
      mb="sm"
      styles={{ root: { boxShadow: 'none' } }}
    >
      {/* Accordion Header */}
      <UnstyledButton
        onClick={handleToggle}
        style={{ width: '100%' }}
      >
        <Group
          justify="space-between"
          p="md"
          style={{
            borderBottom: expanded ? '1px solid var(--mantine-color-gray-2)' : 'none',
          }}
        >
          <Group gap="md">
            {/* Expand/Collapse Icon */}
            {expanded ? (
              <ChevronDown size={18} color="var(--mantine-color-dimmed)" />
            ) : (
              <ChevronRight size={18} color="var(--mantine-color-dimmed)" />
            )}

            {/* Phase Name */}
            <Text fw={600} size="sm">
              {phaseName}
            </Text>

            {/* Status Badge */}
            <Badge
              size="xs"
              color={statusConfig.color}
              variant={statusConfig.variant}
              style={{
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                ...(phaseStatus === 'not_started' ? { border: 'none', color: '#888888' } : {}),
              }}
            >
              {statusConfig.label}
            </Badge>
          </Group>

          {/* Progress Bar, Count, and Expand All */}
          <Group gap="md" style={{ minWidth: 150 }}>
            <Progress
              value={progressPercent}
              size="sm"
              styles={{
                root: { flex: 1, minWidth: 80 },
                section: { backgroundColor: progressBarColor },
              }}
            />
            <Text size="xs" c="dimmed" fw={500}>
              {isFilterActive
                ? `${filteredCount} result${filteredCount !== 1 ? 's' : ''}`
                : `${completedCount}/${totalCount}`}
            </Text>
            {/* MED-769: Per-phase expand all / collapse all */}
            {onExpandAllTasks && onCollapseAllTasks && tasks.some((t) => t.subtask_count > 0) && (
              <Button
                variant="default"
                size="compact-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  if (allTasksExpanded) {
                    onCollapseAllTasks();
                  } else {
                    onExpandAllTasks();
                  }
                }}
              >
                {allTasksExpanded ? 'Collapse all' : 'Expand all'}
              </Button>
            )}
          </Group>
        </Group>
      </UnstyledButton>

      {/* Accordion Content */}
      <Collapse in={expanded}>
        <Stack gap={0} pb="sm">
          {/* Sub-groups with tasks */}
          {sortedSubGroups.length > 0 ? (
            sortedSubGroups.map(([groupName, groupTasks]) => (
              <SubGroupSection
                key={groupName}
                name={groupName}
                tasks={groupTasks}
                cohortStatus={groupTasks[0]?.sub_group_cohort_status}
                onToggleComplete={onToggleComplete}
                onTaskClick={onTaskClick}
                onEditTask={onEditTask}
                onDeleteTask={onDeleteTask}
                onDuplicateTask={onDuplicateTask}
                expandedTaskIds={expandedTaskIds}
                loadingTaskIds={loadingTaskIds}
                subtaskCache={subtaskCache}
                onToggleExpand={onToggleExpand}
                cohortId={cohortId}
              />
            ))
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No tasks in this phase
            </Text>
          )}

          {/* Add Task Link */}
          {onAddTask && (
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              leftSection={<Plus size={14} />}
              onClick={() => onAddTask(phase)}
              style={{ alignSelf: 'flex-start', marginLeft: 'var(--mantine-spacing-sm)', marginTop: 'var(--mantine-spacing-xs)' }}
            >
              Add Task
            </Button>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}

export default PhaseAccordion;
